import React, { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../../api/client';
import { Badge } from '../../ui/components/Badge';
import { Button } from '../../ui/components/Button';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { FeatherX, FeatherChevronLeft, FeatherChevronRight } from '@subframe/core';
import { format } from 'date-fns';
import { is3DModelUrl } from '../../utils/media';

interface MetadataHistoryEntry {
  id: string;
  version: number;
  metadata_uri: string | null;
  image_url: string | null;
  media_url: string | null;
  thumbnail_url: string | null;
  metadata: Record<string, any> | null;
  name: string | null;
  description: string | null;
  traits: Array<{ trait_type: string; value: string | number }> | null;
  transaction_hash: string | null;
  modified_by: string | null;
  created_at: string;
}

interface CurrentNFTData {
  name: string | null;
  description: string | null;
  image_url: string | null;
  media_url: string | null;
  thumbnail_url: string | null;
  metadata_uri: string | null;
  metadata: Record<string, any> | null;
  traits: Array<{ trait_type: string; value: string | number }> | null;
  metadata_updated_at: string | null;
}

interface MetadataHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  nftTokenId: string;
  nftName: string;
  currentVersion: number;
  currentNFT: CurrentNFTData;
  nftCreatedAt?: string;
}

export function MetadataHistoryModal({
  isOpen,
  onClose,
  nftTokenId,
  nftName,
  currentVersion,
  currentNFT,
  nftCreatedAt,
}: MetadataHistoryModalProps) {
  const [allVersions, setAllVersions] = useState<MetadataHistoryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedIndex, setSelectedIndex] = useState(0);

  useEffect(() => {
    if (!isOpen) return;
    setLoading(true);
    setError(null);

    apiClient
      .get<{ success: boolean; data: MetadataHistoryEntry[] }>(
        `/nfts/${nftTokenId}/metadata-history`
      )
      .then((res) => {
        const history = res.data || [];
        // Synthesize current version entry from live NFT data
        const currentEntry: MetadataHistoryEntry = {
          id: 'current',
          version: currentVersion,
          metadata_uri: currentNFT.metadata_uri,
          image_url: currentNFT.image_url,
          media_url: currentNFT.media_url,
          thumbnail_url: currentNFT.thumbnail_url,
          metadata: currentNFT.metadata,
          name: currentNFT.name,
          description: currentNFT.description,
          traits: currentNFT.traits,
          transaction_hash: null,
          modified_by: null,
          created_at: currentNFT.metadata_updated_at || new Date().toISOString(),
        };
        // history is ordered version DESC; prepend current as newest
        setAllVersions([currentEntry, ...history]);
        setSelectedIndex(0);
      })
      .catch((err) => {
        setError(err.message || 'Failed to load metadata history');
      })
      .finally(() => {
        setLoading(false);
      });
  }, [isOpen, nftTokenId, currentVersion, currentNFT]);

  // Close on Escape key
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    },
    [onClose]
  );

  useEffect(() => {
    if (!isOpen) return;
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, handleKeyDown]);

  if (!isOpen) return null;

  const selected = allVersions[selectedIndex];

  // Compute date range for the selected version
  const getDateRange = (index: number): { start: string; end: string | null } => {
    const isOldest = index === allVersions.length - 1;
    const isCurrent = index === 0;
    // Start: for oldest version use NFT mint date, otherwise the older entry's created_at
    const start = isOldest
      ? (nftCreatedAt || allVersions[index].created_at)
      : allVersions[index + 1].created_at;
    // End: null means "current"
    const end = isCurrent ? null : allVersions[index].created_at;
    return { start, end };
  };

  const formatShortDate = (dateStr: string) => format(new Date(dateStr), 'M/d/yy');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      {/* Dialog */}
      <div className="relative z-10 w-full max-w-lg rounded-lg border border-solid border-neutral-border bg-default-background shadow-xl max-h-[90vh] overflow-auto">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-solid border-neutral-border px-6 py-4">
          <div className="flex items-center gap-2">
            <h2 className="text-heading-3 font-heading-3 text-default-font">
              Metadata History
            </h2>
            <Badge variant="neutral">v{selected?.version ?? currentVersion}</Badge>
          </div>
          <button
            onClick={onClose}
            className="rounded p-1 hover:bg-neutral-border/50 transition-colors"
          >
            <FeatherX className="h-5 w-5 text-subtext-color" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-4">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <LoadingSpinner />
            </div>
          ) : error ? (
            <div className="text-center py-8 text-error-700">{error}</div>
          ) : allVersions.length === 0 ? (
            <div className="text-center py-8 text-subtext-color">
              No previous versions found.
            </div>
          ) : (
            <>
              {/* Navigation */}
              <div className="flex items-center justify-between mb-1">
                <Button
                  variant="neutral-tertiary"
                  size="small"
                  disabled={selectedIndex >= allVersions.length - 1}
                  onClick={() => setSelectedIndex((i) => i + 1)}
                  icon={<FeatherChevronLeft className="h-4 w-4" />}
                >
                  Older
                </Button>
                <span className="text-body-bold font-body-bold text-default-font">
                  Version {selected?.version || '?'} of {currentVersion}
                </span>
                <Button
                  variant="neutral-tertiary"
                  size="small"
                  disabled={selectedIndex <= 0}
                  onClick={() => setSelectedIndex((i) => i - 1)}
                  icon={<FeatherChevronRight className="h-4 w-4" />}
                >
                  Newer
                </Button>
              </div>
              {/* Date range */}
              {selected && (
                <div className="text-center text-caption text-subtext-color mb-4">
                  {(() => {
                    const { start, end } = getDateRange(selectedIndex);
                    return end === null
                      ? `${formatShortDate(start)} \u2013 current`
                      : `${formatShortDate(start)} \u2013 ${formatShortDate(end)}`;
                  })()}
                </div>
              )}

              {/* Selected version details */}
              {selected && (
                <div className="flex flex-col gap-4">
                  {/* Image */}
                  {(selected.thumbnail_url || selected.media_url || selected.image_url) && (
                    <div className="flex justify-center">
                      {is3DModelUrl(selected.media_url) ? (
                        <div className="max-h-48 w-48 h-48 rounded-md flex items-center justify-center bg-neutral-100">
                          <div className="flex flex-col items-center gap-1">
                            <span style={{ fontSize: '32px' }}>🧊</span>
                            <span className="text-caption font-caption text-subtext-color">3D Model</span>
                          </div>
                        </div>
                      ) : (
                        <img
                          src={selected.thumbnail_url || selected.media_url || selected.image_url || ''}
                          alt={selected.name || `Version ${selected.version}`}
                          className="max-h-48 rounded-md object-contain"
                        />
                      )}
                    </div>
                  )}

                  {/* Name & Description */}
                  <div className="flex flex-col gap-1">
                    <span className="text-body-bold font-body-bold text-default-font">
                      {selected.name || 'Unnamed'}
                    </span>
                    {selected.description && (
                      <span className="text-body text-subtext-color line-clamp-3">
                        {selected.description}
                      </span>
                    )}
                  </div>

                  {/* Traits */}
                  {selected.traits && selected.traits.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {selected.traits.map((trait, i) => (
                        <div
                          key={i}
                          className="flex flex-col rounded-md border border-solid border-neutral-border px-3 py-1.5"
                        >
                          <span className="text-caption text-subtext-color">
                            {trait.trait_type}
                          </span>
                          <span className="text-body-bold font-body-bold text-default-font">
                            {trait.value}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Additional images — Metaplex-style `properties.files` minus the primary.
                      Each snapshot was taken after enrichment cached media to S3, so the
                      cdn_url fields in the JSONB point at version-pinned CloudFront URLs
                      (e.g. /nfts/<id>/v2/original.jpg) that still resolve for older versions. */}
                  {(() => {
                    const files = (selected.metadata as any)?.properties?.files;
                    const primaryUri = (selected.metadata as any)?.image as string | undefined;
                    if (!Array.isArray(files)) return null;

                    // Resolve Metaplex-style relative URIs against the primary's
                    // IPFS directory before comparing — otherwise a bare filename
                    // like `xdawg4867.png` wouldn't match `ipfs://<cid>/xdawg4867.png`
                    // and would render as a broken "additional image" tile.
                    // Mirrors the logic in NFTDetailPage#getMediaSlides.
                    const primaryIpfsBase = (() => {
                      if (typeof primaryUri !== 'string' || !primaryUri.startsWith('ipfs://')) return null;
                      const lastSlash = primaryUri.lastIndexOf('/');
                      if (lastSlash <= 'ipfs://'.length) return null;
                      return primaryUri.slice(0, lastSlash + 1);
                    })();
                    const resolveRelative = (raw: string): string => {
                      if (/^(ipfs|https?|ar):\/\//i.test(raw)) return raw;
                      if (raw.includes(':')) return raw;
                      if (!primaryIpfsBase) return raw;
                      const cleaned = raw.replace(/^\.\//, '');
                      if (cleaned.startsWith('/') || cleaned.includes('..')) return raw;
                      return primaryIpfsBase + cleaned;
                    };

                    const alts = files.filter((f: any) => {
                      if (!f || typeof f !== 'object' || typeof f.uri !== 'string') return false;
                      if (resolveRelative(f.uri) === primaryUri) return false;
                      const type = typeof f.type === 'string' ? f.type : '';
                      return type.startsWith('image/') || /\.(png|jpe?g|gif|webp|avif|bmp)(\?|#|$)/i.test(f.uri);
                    });
                    if (alts.length === 0) return null;
                    const toHttp = (uri: string) =>
                      uri.startsWith('ipfs://')
                        ? `https://ipfs.io/ipfs/${uri.slice('ipfs://'.length)}`
                        : uri;
                    return (
                      <div className="flex flex-col gap-2">
                        <span className="text-body-bold font-body-bold text-default-font">
                          Additional images
                        </span>
                        <div className="grid grid-cols-4 gap-2">
                          {alts.map((f: any, i: number) => {
                            const displayUri = f.cdn_url || toHttp(resolveRelative(f.uri));
                            return (
                              <a
                                key={i}
                                href={displayUri}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="block aspect-square overflow-hidden rounded-md border border-solid border-neutral-border bg-neutral-50 hover:border-brand-400 transition-colors"
                              >
                                <img
                                  src={displayUri}
                                  alt={`Additional image ${i + 1}`}
                                  className="h-full w-full object-cover"
                                  loading="lazy"
                                />
                              </a>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
